import {
  RFC1035_LABEL_REGEX,
  validateDomainRfc1035,
} from '~/utils/domains/validations';

/** RFC 1123 hostname: one or more labels (matches backend vps validation). */
const RFC1123_HOSTNAME_REGEX =
  /^(?:[A-Za-z0-9][A-Za-z0-9-]{0,61}[A-Za-z0-9]?){1}(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

/** RFC 1123 with at least 3 labels (matches backend vps_cpanel validation). */
const RFC1123_VPS_CPANEL_HOSTNAME_REGEX =
  /^(?:[A-Za-z0-9][A-Za-z0-9-]{0,61}[A-Za-z0-9]?){1}(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?){2,}$/;

const RFC1123_LABEL_REGEX = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;

export const isVpsCpanelServerType = (serverType?: string | null): boolean =>
  serverType === 'vps_cpanel' || Boolean(serverType?.endsWith('_cpanel'));

export const getServerSetupHostnameRegex = (
  serverType?: string | null,
): RegExp =>
  isVpsCpanelServerType(serverType)
    ? RFC1123_VPS_CPANEL_HOSTNAME_REGEX
    : RFC1123_HOSTNAME_REGEX;

export const buildDefaultServerHostname = (
  serverId: string | number | null | undefined,
  domain: string,
  serverType?: string | null,
): string => {
  const normalizedDomain = domain.replace(/\s/g, '');
  if (!normalizedDomain) {
    return '';
  }

  const hasServerId = serverId != null && serverId !== '';
  if (hasServerId) {
    return `server-${serverId}.${normalizedDomain}`;
  }

  // cpanel server types require the server id; without one we can't build a default.
  if (serverType?.endsWith('_cpanel')) {
    return '';
  }
  return `server.${normalizedDomain}`;
};

export type ServerSetupDomainErrorKey =
  | 'domainErrorNoTld'
  | 'domainErrorInvalidChars'
  | 'domainErrorHyphenEdge'
  | 'domainErrorFormat';

export type ServerSetupHostnameErrorKey =
  | 'hostnameErrorRequired'
  | 'hostnameErrorUnderscore'
  | 'hostnameErrorStartChar'
  | 'hostnameErrorLabelLength'
  | 'hostnameErrorHyphenEdge'
  | 'hostnameErrorMinLabels'
  | 'hostnameErrorFormat';

export const getServerSetupDomainErrorKey = (
  domain: string,
): ServerSetupDomainErrorKey | null => {
  const trimmed = domain.trim();
  if (!trimmed) {
    return null;
  }

  if (/\s/.test(trimmed)) {
    return 'domainErrorInvalidChars';
  }

  if (trimmed.startsWith('-') || trimmed.endsWith('-')) {
    return 'domainErrorHyphenEdge';
  }

  if (!trimmed.includes('.')) {
    return 'domainErrorNoTld';
  }

  const labels = trimmed.split('.').filter(Boolean);
  if (
    labels.length !== trimmed.split('.').length ||
    labels.some((label) => label.length > 63)
  ) {
    return 'domainErrorFormat';
  }

  if (
    labels.some(
      (label) =>
        label.startsWith('-') ||
        label.endsWith('-') ||
        !RFC1035_LABEL_REGEX.test(label),
    )
  ) {
    return 'domainErrorFormat';
  }

  if (!validateDomainRfc1035(trimmed)) {
    return 'domainErrorFormat';
  }

  return null;
};

export const getServerSetupHostnameErrorKey = (
  hostname: string,
  serverType?: string | null,
): ServerSetupHostnameErrorKey | null => {
  const trimmed = hostname.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes('_')) {
    return 'hostnameErrorUnderscore';
  }

  if (!/^[A-Za-z]/.test(trimmed)) {
    return 'hostnameErrorStartChar';
  }

  const rawLabels = trimmed.split('.');
  const labels = rawLabels.filter((label) => label.length > 0);
  if (labels.length !== rawLabels.length) {
    return 'hostnameErrorFormat';
  }

  if (labels.some((label) => label.startsWith('-') || label.endsWith('-'))) {
    return 'hostnameErrorHyphenEdge';
  }

  if (labels.some((label) => label.length > 63)) {
    return 'hostnameErrorLabelLength';
  }

  if (labels.some((label) => !RFC1123_LABEL_REGEX.test(label))) {
    return 'hostnameErrorFormat';
  }

  if (isVpsCpanelServerType(serverType) && labels.length < 3) {
    return 'hostnameErrorMinLabels';
  }

  if (!getServerSetupHostnameRegex(serverType).test(trimmed)) {
    return 'hostnameErrorFormat';
  }

  return null;
};
